import {
  AddOutlined,
  RemoveOutlined,
  ShoppingCart,
  ShoppingCartOutlined,
} from "@mui/icons-material";
import { Button, IconButton, Stack } from "@mui/material";
import { Box } from "@mui/system";
import React from "react";
import { useHistory } from "react-router-dom";
import "./Cart.css";
// Definition of Data Structures used
/**
 * @typedef {Object} Product - Data on product available to buy
 *
 * @property {string} name - The name or title of the product
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} _id - Unique ID for the product
 */

/**
 * @typedef {Object} CartItem -  - Data on product added to cart
 *
 * @property {string} name - The name or title of the product in cart
 * @property {string} qty - The quantity of product added to cart
 * @property {string} category - The category that the product belongs to
 * @property {number} cost - The price to buy the product
 * @property {number} rating - The aggregate rating of the product (integer out of five)
 * @property {string} image - Contains URL for the product image
 * @property {string} productId - Unique ID for the product
 */
/**
 * Returns the complete data on all products in cartData by searching in productsData
 *
 * @param { Array.<{ productId: String, qty: Number }> } cartData
 *    Array of objects with productId and quantity of products in cart
 *
 * @param { Array.<Product> } productsData
 *    Array of objects with complete data on all available products
 *
 * @returns { Array.<CartItem> }
 *    Array of objects with complete data on products in cart
 *
 */
export const generateCartItemsFrom = (cartData, productsData) => {
  const cartItems = [];

  for (const cartItem of cartData) {
    const product = productsData.find((p) => p._id === cartItem.productId);

    if (product) {
      cartItems.push({
        name: product.name,
        qty: cartItem.qty,
        cost: product.cost,
        category: product.category,
        rating: product.rating,
        image: product.image,
        productId: product._id,
      });
    }
  }
  return cartItems;
};

/**
 * Get the total value of all products added to the cart
 *
 * @param { Array.<CartItem> } items
 *    Array of objects with complete data on products added to the cart
 *
 * @returns { Number }
 *    Value of all items in the cart
 *
 */
export const getTotalCartValue = (items = []) => {
  let total = 0;
  items.forEach((item) => {
    total += parseFloat(item.cost) * parseFloat(item.qty);
  });
  return total;
};

const getTotalItems = (items = []) => {
  let count = 0;
  items.forEach((item) => {
    count = count + 1;
  });
  return count;
};

/**
 * Component to display the current quantity for a product and + and - buttons to update product quantity on cart
 *
 * @param {Number} value
 *    Current quantity of product in cart
 *
 * @param {Function} handleAdd
 *    Handler function which adds 1 more of a product to cart
 *
 * @param {Function} handleDelete
 *    Handler function which reduces the quantity of a product in cart by 1
 *
 *
 */

const ItemQuantity = ({ value, handleAdd, handleDelete, isReadOnly }) => {
  return (
    <Stack direction="row" alignItems="center">
      {isReadOnly === false ? (
        <>
          <IconButton size="small" color="primary" onClick={handleDelete}>
            <RemoveOutlined />
          </IconButton>
          <Box padding="0.5rem" data-testid="item-qty">
            {value}
          </Box>
          <IconButton size="small" color="primary" onClick={handleAdd}>
            <AddOutlined />
          </IconButton>
        </>
      ) : (
        <Box padding="0.5rem" data-testid="item-qty">
          Qty: {value}
        </Box>
      )}
    </Stack>
  );
};

/**
 * Component to display the Cart view
 *
 * @param { Array.<Product> } products
 *    Array of objects with complete data of all available products
 *
 * @param { Array.<Product> } items
 *    Array of objects with complete data on products in cart
 *
 * @param {Function} handleDelete
 *    Current quantity of product in cart
 *
 *
 */
const Cart = ({ products, items = [], handleQuantity, isReadOnly }) => {
  const history = useHistory();  
  if (items && !items.length) {
    return (
      <Box className="cart empty">
        <ShoppingCartOutlined className="empty-cart-icon" />
        <Box color="#aaa" textAlign="center">
          Cart is empty. Add more items to the cart to checkout.
        </Box>
      </Box>
    );
  }
  const cartItems = generateCartItemsFrom(items, products);
  return (
    <>
      <Box className="cart">
        {
          /* TODO: CRIO_TASK_MODULE_CART - Display view for each cart item with non-zero quantity */
          cartItems.map((item, index) => (
            <Box
              key={index}
              display="flex"
              alignItems="flex-start"
              padding="1rem"
            >
              <Box className="image-container">
                <img
                  // Add product image
                  src={item.image}
                  // Add product name as alt eext
                  alt={item.name}
                  width="100%"
                  height="100%"
                />
              </Box>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                height="6rem"
                paddingX="1rem"
              >
                <div>{item.name}</div>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <ItemQuantity
                    isReadOnly={isReadOnly}
                    value={item.qty}
                    // Add required props by checking implementation
                    handleAdd={() =>
                      handleQuantity((item.qty ?? 0) + 1, item.productId)
                    }
                    handleDelete={() =>
                      handleQuantity(item.qty - 1, item.productId)
                    }
                  />
                  <Box padding="0.5rem" fontWeight="700">
                    ${item.cost}
                  </Box>
                </Box>
              </Box>
            </Box>
          ))
        }

        <Box
          padding="1rem"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box color="#3C3C3C" alignSelf="center">
            Order total
          </Box>
          <Box
            color="#3C3C3C"
            fontWeight="700"
            fontSize="1.5rem"
            alignSelf="center"
            data-testid="cart-total"
          >
            ${getTotalCartValue(cartItems)}
          </Box>
        </Box>
        {isReadOnly === false ? (
          <Box display="flex" justifyContent="flex-end" className="cart-footer">
            <Button
              color="primary"
              variant="contained"
              startIcon={<ShoppingCart />}
              className="checkout-btn"
              onClick={() => history.push("/checkout")}
            >
              Checkout
            </Button>
          </Box>
        ) : null}
      </Box>

      {isReadOnly === true ? (
        <Box className="cart" padding="1rem">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box
              color="#3C3C3C"
              fontSize="1.5rem"
              fontWeight="700"
              alignSelf="center"
            >
              Order Details
            </Box>
          </Box>
          <Box
            my={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box color="#3C3C3C" alignSelf="center">
              Product
            </Box>
            <Box alignSelf="center" data-testid="cart-total">
              {getTotalItems(cartItems)}
            </Box>
          </Box>
          <Box
            my={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box color="#3C3C3C" alignSelf="center">
              Subtotal
            </Box>
            <Box alignSelf="center" data-testid="cart-total">
              ${getTotalCartValue(cartItems)}
            </Box>
          </Box>
          <Box
            my={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box color="#3C3C3C" alignSelf="center">
              Shipping charges
            </Box>
            <Box alignSelf="center" data-testid="cart-total">
              $0
            </Box>
          </Box>
          <Box
            my={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box
              color="#3C3C3C"
              alignSelf="center"
              fontSize="1.5rem"
              fontWeight="700"
            >
              Total
            </Box>
            <Box
              alignSelf="center"
              data-testid="cart-total"
              fontSize="1.5rem"
              fontWeight="700"
            >
              ${getTotalCartValue(cartItems)}
            </Box>
          </Box>
        </Box>
        ) : null
      }
    </>
  );
};

export default Cart;
